import { Link } from "react-router-dom";
import { useState } from "react";
import { copy } from "../copy";
import Icon from "../components/Icon";
import { shortDate, type AdminContest, type Queue } from "./api";
import { Chip, ghostButton, inputClass, primaryButton, smallButton, tdClass, thClass } from "./ui";

export type RowActions = {
  busy: boolean;
  selectWinner: (contest: AdminContest, userID: string) => void;
  notifyVote: (contest: AdminContest) => void;
  updateTallies: (contest: AdminContest) => void;
  setPublished: (contest: AdminContest, published: boolean) => void;
  remove: (contest: AdminContest) => void;
};

// One contest in a judging queue. Every control here calls an endpoint that
// exists; the template's fingerprint, Flag, and Sync Reviews are gone.
export default function ContestRow({ contest, queue, actions }: { contest: AdminContest; queue: Queue; actions: RowActions }) {
  const t = copy.admin.row;
  const [pick, setPick] = useState("");
  const judging = queue === "ready" || queue === "votes";
  const canPickWinner = !contest.winner && contest.winners_count === 1 && contest.candidates.length > 0;
  const facts: [string, string][] = [
    [t.created, shortDate(contest.created_at)],
    [t.starts, shortDate(contest.start_at)],
    [t.ends, shortDate(contest.end_at)],
    [t.duration, contest.duration],
    [t.views, String(contest.views)],
    [t.reservations, `${contest.reservations} / ${contest.seats}`],
  ];

  return (
    <article data-testid="contest-row" className="border-b border-ground-edge py-7 first:pt-0 last:border-b-0">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_280px] lg:gap-8">
        <div className="min-w-0">
          <div className="flex flex-col gap-4 sm:flex-row">
            <div className="shrink-0">
              {contest.cover_url ? (
                <img src={contest.cover_url} alt="" className="h-28 w-28 rounded-lg border border-ground-edge object-cover" />
              ) : (
                <span className="flex h-28 w-28 items-center justify-center rounded-lg border border-ground-edge text-3xl text-chrome-dim">
                  <Icon name="disc" />
                </span>
              )}
            </div>
            <div className="min-w-0">
              {/* Go serves the contest page; a full page load. */}
              <a
                href={`/contest/${contest.slug || contest.id}`}
                className="bf-focusable text-base font-bold text-gold transition-colors hover:text-gold-light"
              >
                {contest.title}
              </a>
              <p className="mt-1 break-all text-[11px] text-chrome-dim">
                {t.id} <span data-testid="contest-id" className="select-all text-chrome">{contest.id}</span>
              </p>
              <p className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] font-bold uppercase tracking-[0.12em]">
                <Link to={`/admin/contests/${contest.id}`} className="bf-focusable text-chrome-dim hover:text-chrome">
                  {t.edit}
                </Link>
                <a href={`/contest/judge/${contest.id}`} className="bf-focusable text-chrome-dim hover:text-chrome">
                  {t.judgePage}
                </a>
              </p>
              <p className="mt-2 text-[13px] text-chrome">
                {[contest.challenge, contest.genre].filter(Boolean).join(" · ")}
              </p>
              <p className="bf-tabular mt-1 text-[13px] text-chrome-dim">
                {t.entry} <span className="text-chrome">{contest.admission.display}</span> · {t.prize}{" "}
                <span className="text-money">{contest.cash_prize.display}</span> · {t.pot}{" "}
                <span className="text-chrome">{contest.pot.display}</span>
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Chip tone={contest.published ? "money" : "dim"}>{contest.published ? t.published : t.unpublished}</Chip>
                {contest.admin_created && <Chip tone="dim">{t.seeded}</Chip>}
                <Chip tone="gold">{t.seats(contest.contestants, contest.seats)}</Chip>
                <Chip tone="dim">{t.submissions(contest.submissions)}</Chip>
              </div>
              {contest.preview_url && (
                <audio
                  controls
                  preload="none"
                  src={contest.preview_url}
                  aria-label={t.preview(contest.title)}
                  className="mt-3 h-9 w-full max-w-sm"
                />
              )}
            </div>
          </div>

          <div className="bf-no-scrollbar mt-5 overflow-x-auto rounded-lg border border-ground-edge">
            <table className="w-full min-w-[420px] border-collapse">
              <thead className="border-b border-ground-edge">
                <tr>
                  <th className={thClass}>{t.producer}</th>
                  <th className={thClass}>{t.votes}</th>
                  <th className={thClass}>{t.rating}</th>
                </tr>
              </thead>
              <tbody>
                {contest.tallies.length === 0 && (
                  <tr>
                    <td className={`${tdClass} text-chrome-dim`} colSpan={3}>
                      {t.noTallies}
                    </td>
                  </tr>
                )}
                {contest.tallies.map((tally) => (
                  <tr key={tally.username} className="border-t border-ground-edge first:border-t-0">
                    <td className={tdClass}>
                      <span className="block font-medium">{tally.username}</span>
                      <span className="block text-[12px] text-chrome-dim">{tally.email}</span>
                    </td>
                    <td className={`${tdClass} bf-tabular`}>{tally.votes}</td>
                    <td className={`${tdClass} bf-tabular`}>{tally.ratings}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {queue === "votes" && (
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <NameList title={t.didNotVote} names={contest.did_not_vote} />
              <NameList title={t.voted} names={contest.voters.map((v) => v.username)} />
            </div>
          )}
        </div>

        <aside className="min-w-0">
          <ul className="space-y-1.5 text-[12px] text-chrome-dim">
            {facts
              .filter(([, v]) => v !== "")
              .map(([k, v]) => (
                <li key={k}>
                  {k}: <span className="bf-tabular text-chrome">{v}</span>
                </li>
              ))}
          </ul>

          {judging && (
            <>
              <button type="button" disabled={actions.busy} onClick={() => actions.notifyVote(contest)} className={`${primaryButton} mt-4 w-full`}>
                <Icon name="megaphone" className="text-sm" />
                {t.notifyVote}
              </button>
              <p className="mt-1.5 text-[11px] text-chrome-dim">
                {contest.vote_emails_sent_at ? t.sent(shortDate(contest.vote_emails_sent_at)) : t.notSent}
              </p>
              <button type="button" disabled={actions.busy} onClick={() => actions.updateTallies(contest)} className={`${ghostButton} mt-3 w-full`}>
                <Icon name="refresh" className="text-sm" />
                {t.updateTallies}
              </button>
            </>
          )}

          {canPickWinner && queue !== "won" && (
            <div className="mt-4">
              <select
                aria-label={t.pickWinner(contest.title)}
                value={pick}
                onChange={(e) => setPick(e.target.value)}
                className={`${inputClass} cursor-pointer`}
              >
                <option value="">{t.chooseWinner}</option>
                {contest.candidates.map((c) => (
                  <option key={c.user_id} value={c.user_id}>
                    {c.username}
                  </option>
                ))}
              </select>
              <button
                type="button"
                disabled={actions.busy || pick === ""}
                onClick={() => actions.selectWinner(contest, pick)}
                className={`${smallButton} mt-2 w-full border-gold/50 py-2.5 text-gold hover:bg-gold hover:text-ground`}
              >
                <Icon name="trophy" className="mr-1.5 text-sm" />
                {t.selectWinner}
              </button>
            </div>
          )}
          {!contest.winner && contest.winners_count > 1 && queue !== "won" && (
            <p className="mt-4 text-[12px] leading-snug text-chrome-dim">{t.placementsNote}</p>
          )}
          {contest.winner && (
            <p className="mt-4 flex items-center gap-2 rounded-md border border-money/40 bg-money/10 px-3 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-money">
              <Icon name="trophy" className="text-sm" />
              {t.winner(contest.winner.username)}
            </p>
          )}

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              disabled={actions.busy}
              onClick={() => actions.setPublished(contest, !contest.published)}
              className={`${smallButton} border-ground-edge text-chrome-dim hover:border-gold/50 hover:text-gold`}
            >
              {contest.published ? t.unpublish : t.publish}
            </button>
            {!contest.winner && contest.submissions === 0 && (
              <button
                type="button"
                disabled={actions.busy}
                onClick={() => actions.remove(contest)}
                className={`${smallButton} border-alert/50 text-alert hover:bg-alert hover:text-ground`}
              >
                <Icon name="deleteBin" className="mr-1 text-sm" />
                {t.delete}
              </button>
            )}
          </div>
        </aside>
      </div>

      {queue !== "won" && (
        <p className="mt-5 text-[11px] uppercase tracking-[0.14em] text-gold">{queue === "review" ? t.reviewNote : t.readyNote}</p>
      )}
    </article>
  );
}

function NameList({ title, names }: { title: string; names: string[] }) {
  return (
    <div className="rounded-lg border border-ground-edge p-3">
      <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-chrome-dim">{title}</p>
      {names.length === 0 ? (
        <p className="mt-2 text-[12px] text-chrome-dim">{copy.admin.row.nobody}</p>
      ) : (
        <ol className="mt-2 list-decimal space-y-0.5 pl-5 text-[12px] text-chrome">
          {names.map((n, i) => (
            <li key={`${n}-${i}`}>{n}</li>
          ))}
        </ol>
      )}
    </div>
  );
}
